// First-party analytics: sessions, events and the context that makes them
// useful (device, language, timezone, referrer, inbound UTMs). Events are
// batched to the track-events edge function, which stamps the country from
// the request and writes analytics_events; the same call mirrors to GA4
// through the old wrapper so nothing that was tracked before is lost.
//
// Privacy: no cookies. The session id lives in sessionStorage (30-minute
// idle timeout). A persistent visitor id lives in localStorage only while the
// analytics preference allows it and is dropped the moment it is declined.
// The IP address is never stored; the edge function keeps only the country.
import { track as gaTrack } from "@/lib/analytics";
import { hasAnalyticsConsent, onConsentChanged } from "@/lib/cookieConsent";

export const TRACKED_EVENTS = [
  "page_view",
  "search",
  "contact_request_sent",
  "request_accepted",
  "rental_marked",
  "request_declined",
  "whatsapp_click",
  "phone_reveal",
  "signup_completed",
  "profile_completed",
  "wizard_step",
  // An owner skipped an optional page of the listing wizard (Oct 26).
  "wizard_skip",
  // A listing photo couldn't be read or uploaded: stage pick | read | upload
  // (Oct 27, a phone lost a landlord's photos).
  "photo_unreadable",
  "listing_published",
  "concierge_opened",
  "concierge_message",
  "concierge_handoff",
  "alert_signup_view",
  "alert_signup_submit",
  "claim_view",
  "claim_started",
  "claim_declined",
  "claim_completed",
  "share_click",
  "directions_click",
  "gallery_open",
  "listing_delisted",
  "listing_paused",
  "listing_rented",
  "listing_republished",
  "listing_deleted",
  "availability_updated",
  "request_reopened",
  "client_error",
  "oauth_started",
  "oauth_returned",
  // Paid extras funnel (Oct 20): card seen is logged server side (exposures).
  "offer_clicked",
  "checkout_started",
  "checkout_canceled",
  "purchase_completed",
] as const;
export type TrackedEvent = (typeof TRACKED_EVENTS)[number];

export type Device = "mobile" | "tablet" | "desktop";

export interface TrackingContext {
  sessionId: string;
  visitorId: string | null;
  device: Device;
  lang: string | null;
  tz: string | null;
  referrerHost: string | null;
  utm: Record<string, string> | null;
  viewportW: number | null;
  /** The visitor's country as our edge saw it (/__ctx), once fetched this session. */
  country?: string | null;
}

export interface QueuedEvent {
  event: TrackedEvent;
  path: string;
  props: Record<string, unknown>;
  at: string;
}

const SESSION_KEY = "msth_session_v1";
const VISITOR_KEY = "msth_visitor_v1";
export const SESSION_IDLE_MS = 30 * 60 * 1000;
const FLUSH_DELAY_MS = 1500;
const MAX_BATCH = 20;
// utm_id is the short link a visit came through (/go/<code>, Admin -> Links).
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id"];

interface SessionRecord {
  id: string;
  last: number;
  referrerHost: string | null;
  utm: Record<string, string> | null;
}

const uuid = (): string => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
  }
};

// --- pure helpers (tested) --------------------------------------------------

export const classifyDevice = (userAgent: string, width: number): Device => {
  const ua = userAgent.toLowerCase();
  if (/ipad|tablet|(android(?!.*mobile))/.test(ua)) return "tablet";
  if (/mobi|iphone|ipod|android|windows phone|opera mini/.test(ua)) return "mobile";
  if (width > 0 && width < 640) return "mobile";
  if (width > 0 && width < 1024 && /touch/.test(ua)) return "tablet";
  return "desktop";
};

export const parseUtm = (search: string): Record<string, string> | null => {
  const params = new URLSearchParams(search);
  const out: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key)?.trim();
    if (v) out[key.slice(4)] = v.slice(0, 80);
  }
  return Object.keys(out).length ? out : null;
};

// The referring site, or null when the referrer is our own host / empty.
export const referrerHostOf = (referrer: string, ownHost: string): string | null => {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).hostname.replace(/^www\./, "").toLowerCase();
    const own = ownHost.replace(/^www\./, "").toLowerCase();
    return host && host !== own ? host : null;
  } catch {
    return null;
  }
};

// Reads or renews the session. Entry context (referrer, UTMs) is captured
// once per session so a mid-session navigation never overwrites it.
export const resolveSession = (
  stored: SessionRecord | null,
  now: number,
  entry: { referrerHost: string | null; utm: Record<string, string> | null },
): { session: SessionRecord; isNew: boolean } => {
  if (stored && now - stored.last <= SESSION_IDLE_MS) {
    return { session: { ...stored, last: now }, isNew: false };
  }
  return { session: { id: uuid(), last: now, referrerHost: entry.referrerHost, utm: entry.utm }, isNew: true };
};

// --- runtime -----------------------------------------------------------------

let tenantId = "";
let currentUserId: string | null = null;
const queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let initialised = false;

const readSession = (): SessionRecord | null => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as SessionRecord) : null;
  } catch {
    return null;
  }
};

// Without analytics consent nothing is stored on the device: the session
// (and its entry UTMs) lives in memory until the visitor accepts.
const writeSession = (s: SessionRecord) => {
  if (!hasAnalyticsConsent()) return;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
  } catch {
    /* storage unavailable, events still carry the in-memory id */
  }
};

let memorySession: SessionRecord | null = null;

const currentSession = (): SessionRecord => {
  const now = Date.now();
  const stored = readSession() ?? memorySession;
  const entry = {
    referrerHost: typeof document !== "undefined" ? referrerHostOf(document.referrer, location.hostname) : null,
    utm: typeof location !== "undefined" ? parseUtm(location.search) : null,
  };
  const { session } = resolveSession(stored, now, entry);
  memorySession = session;
  writeSession(session);
  return session;
};

const visitorId = (): string | null => {
  if (!hasAnalyticsConsent()) return null;
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

const clearVisitorId = () => {
  try {
    localStorage.removeItem(VISITOR_KEY);
  } catch {
    /* ignore */
  }
};

export const getTrackingContext = (): TrackingContext => {
  const session = currentSession();
  const width = typeof window !== "undefined" ? window.innerWidth : 0;
  return {
    sessionId: session.id,
    visitorId: visitorId(),
    device: classifyDevice(typeof navigator !== "undefined" ? navigator.userAgent : "", width),
    lang: typeof navigator !== "undefined" ? navigator.language ?? null : null,
    tz: (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
      } catch {
        return null;
      }
    })(),
    referrerHost: session.referrerHost,
    utm: session.utm,
    viewportW: width || null,
    country: cachedEdgeCountry(),
  };
};

// Set by src/lib/visitorContext.ts once /__ctx has answered (sessionStorage).
const cachedEdgeCountry = (): string | null => {
  try {
    const raw = sessionStorage.getItem("msth_ctx_v1");
    return raw ? (JSON.parse(raw).country ?? null) : null;
  } catch {
    return null;
  }
};

const endpoint = () => `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-events`;

const send = (events: QueuedEvent[], useKeepalive: boolean) => {
  if (!events.length || typeof fetch === "undefined") return;
  const ctx = getTrackingContext();
  const body = JSON.stringify({
    tenant_id: tenantId,
    user_id: currentUserId,
    context: ctx,
    events,
  });
  try {
    void fetch(endpoint(), {
      method: "POST",
      keepalive: useKeepalive,
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body,
    }).catch(() => {});
  } catch {
    /* never let analytics throw */
  }
};

export const flushTracking = (useKeepalive = false) => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  while (queue.length) {
    const batch = queue.splice(0, MAX_BATCH);
    send(batch, useKeepalive);
  }
};

const scheduleFlush = () => {
  if (queue.length >= MAX_BATCH) {
    flushTracking();
    return;
  }
  if (!flushTimer) flushTimer = setTimeout(() => flushTracking(), FLUSH_DELAY_MS);
};

export const initTracking = (opts: { tenantId: string }) => {
  tenantId = opts.tenantId;
  if (initialised || typeof window === "undefined") return;
  initialised = true;
  currentSession();
  window.addEventListener("pagehide", () => flushTracking(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushTracking(true);
  });
  onConsentChanged((c) => {
    if (c.analytics) {
      if (memorySession) writeSession(memorySession);
      return;
    }
    clearVisitorId();
    queue.length = 0;
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
  });
};

export const setTrackingUser = (userId: string | null) => {
  currentUserId = userId;
};

// Records an event (first-party queue + GA4 mirror). Admin paths are never
// tracked: the back office is not the product. Nothing is sent without
// analytics consent (asked first in the EU/EEA, UK and Switzerland; the
// Cookie Policy says our own analytics follow that choice).
export const track = (event: TrackedEvent, props: Record<string, unknown> = {}) => {
  if (typeof window === "undefined") return;
  const path = location.pathname;
  if (path.startsWith("/admin")) return;
  if (!hasAnalyticsConsent()) return;
  gaTrack(event, props);
  queue.push({ event, path: path.slice(0, 200), props, at: new Date().toISOString() });
  scheduleFlush();
};

export const trackPageView = (path: string) => {
  // The investor deck has its own tracking (deck-access); its visits, the PDF
  // service's headless ones included, stay out of the site's numbers.
  if (path.startsWith("/admin") || path === "/deck" || path.startsWith("/deck/")) return;
  track("page_view", { title: typeof document !== "undefined" ? document.title.slice(0, 120) : undefined });
};
